import { useEffect, useState, type FormEvent } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { StatCards } from '../../components/StatCards'
import { API_BASE_URL, getHeaders } from '../../services/NetworkService'
import type { ReportData } from '../../data/types'

// Register Chart.js modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

const barColors = ['rgba(59, 130, 246, 0.8)', 'rgba(16, 185, 129, 0.8)']

// Mock Data for Pie Chart: Types of Registered Cases
// const pieData = {
//   labels: ['Civil Disputes', 'Land Overlap', 'Fraud Charges', 'Contract Breaches'],
//   datasets: [
//     {
//       data: [40, 25, 20, 15],
//       backgroundColor: [
//         '#3b82f6', // blue-500
//         '#f59e0b', // amber-500
//         '#ef4444', // red-500
//         '#8b5cf6', // violet-500
//       ],
//       borderWidth: 1,
//     },
//   ],
// }

export function ReportsView() {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [appliedDates, setAppliedDates] = useState({ startDate: '', endDate: '' })
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const query = new URLSearchParams()
    if (appliedDates.startDate) query.set('startDate', appliedDates.startDate)
    if (appliedDates.endDate) query.set('endDate', appliedDates.endDate)

    const queryString = query.toString()
    const url = `${API_BASE_URL}/documents/report${queryString ? `?${queryString}` : ''}`

    fetch(url, {
      method: 'GET',
      headers: getHeaders(),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Report data could not be loaded.')
        }
        return response.json() as Promise<ReportData>
      })
      .then((data) => setReportData(data))
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === 'AbortError') return
        console.error('Failed to load report data:', error)
        setLoadError('Report data could not be loaded. Please try again.')
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })

    return () => controller.abort()
  }, [appliedDates])

  const handleDateSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    setLoadError(null)
    setAppliedDates({ startDate, endDate })
  }

  const handleClearDates = () => {
    setStartDate('')
    setEndDate('')
    setIsLoading(true)
    setLoadError(null)
    setAppliedDates({ startDate: '', endDate: '' })
  }

  const barData = reportData?.barData
    ? {
        ...reportData.barData,
        datasets: reportData.barData.datasets.map((dataset, index) => ({
          ...dataset,
          backgroundColor: barColors[index % barColors.length],
          borderRadius: 6,
        })),
      }
    : { labels: [], datasets: [] }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900">စာရင်းအင်း ခွဲခြမ်းစိတ်ဖြာမှုနှင့် အစီရင်ခံစာများ</h2>
        <p className="mt-1 text-sm text-slate-500">
          အချိန်နှင့်တပြေးညီ ဖြစ်ရပ်မက်ထရစ်များနှင့် ဒေသဆိုင်ရာ အပလီကေးရှင်း လုပ်ဆောင်ခြင်း ခွဲခြမ်းစိတ်ဖြာမှုများ။
        </p>
      </div>

      <form
        onSubmit={handleDateSearch}
        className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-end"
      >
        <div className="flex-1">
          <label htmlFor="report-start-date" className="mb-1 block text-sm font-medium text-slate-700">
            စတင်ရက်
          </label>
          <input
            id="report-start-date"
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(event) => setStartDate(event.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="report-end-date" className="mb-1 block text-sm font-medium text-slate-700">
            ပြီးဆုံးရက်
          </label>
          <input
            id="report-end-date"
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(event) => setEndDate(event.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-blue-700 px-5 py-2 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          ရှာဖွေရန်
        </button>
        <button
          type="button"
          onClick={handleClearDates}
          className="rounded-md border border-slate-300 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          ရက်စွဲရှင်းလင်းရန်
        </button>
      </form>

      {/* Grid Layout for Graphs */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Bar Chart Container (Spans 2 columns) */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h3 className="mb-4 text-sm font-semibold text-slate-700">ပြည်နယ်/တိုင်းဒေသကြီးအလိုက် အမှုအရေအတွက်</h3>
          <div className="h-80 w-full">
            <Bar 
              data={barData} 
              options={{ 
                responsive: true, 
                maintainAspectRatio: false,
                plugins: { legend: { position: 'top' as const } }
              }} 
            />
          </div>
        </div>

        {/* Pie Chart Container (Spans 1 column) */}
        {/* <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-slate-700">အမျိုးအစားအလိုက် အမှုခွဲဝေမှု</h3>
          <div className="h-80 w-full flex items-center justify-center">
            <Pie 
              data={pieData} 
              options={{ 
                responsive: true, 
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' as const } }
              }} 
            />
          </div>
        </div> */}
      </div>

      <section aria-label="Report summary statistics" className="space-y-4">
        <h3 className="text-base font-semibold text-slate-800">အကျဉ်းချုပ်စာရင်းအင်းများ</h3>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center" role="status" aria-live="polite">
            <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
            <span className="sr-only">Loading report statistics</span>
          </div>
        ) : loadError ? (
          <p role="alert" className="rounded-lg border border-red-200 bg-white p-4 text-sm text-red-700">
            {loadError}
          </p>
        ) : reportData ? (
          <StatCards stats={reportData.summaryStats.filter((stat) => stat.id !== 'active-officers')} />
        ) : null}
      </section>
    </div>
  )
}